import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Eye, Printer, Package, ChevronDown, X } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminOrders = () => {
  const { getAuthHeader } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`, {
        headers: getAuthHeader()
      });
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, 
        { status: newStatus },
        { headers: getAuthHeader() }
      );
      toast.success('Order status updated');
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openInvoice = async (orderId) => {
    try {
      const response = await axios.get(`${API}/admin/orders/${orderId}/invoice`, {
        headers: getAuthHeader()
      });
      setInvoiceOrder(response.data);
    } catch (error) {
      toast.error('Failed to load invoice');
    }
  };

  const printInvoice = () => {
    window.print();
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      processing: 'status-processing',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      cancelled: 'status-cancelled'
    };
    return classes[status] || 'status-pending';
  };

  const filteredOrders = statusFilter === 'all' 
    ? orders 
    : orders.filter(order => order.status === statusFilter);

  const statuses = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl text-charcoal">Orders</h1>
          <p className="text-muted-foreground">Manage customer orders</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="order-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Orders</SelectItem>
            {statuses.map(status => (
              <SelectItem key={status} value={status} className="capitalize">
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 skeleton rounded-xl" />
          ))}
        </div>
      ) : filteredOrders.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">No orders found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order, index) => (
            <motion.div
              key={order.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card>
                <CardContent className="p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-medium text-charcoal">{order.order_number}</h3>
                        <span className={`badge-handcraft ${getStatusBadgeClass(order.status)}`}>
                          {order.status}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {order.customer?.name} • {order.customer?.email}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {order.items?.length} items • ₹{order.total?.toLocaleString()}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(order.created_at).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Select
                        value={order.status}
                        onValueChange={(value) => updateOrderStatus(order.id, value)}
                      >
                        <SelectTrigger className="w-36" data-testid={`order-status-${order.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statuses.map(status => (
                            <SelectItem key={status} value={status} className="capitalize">
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedOrder(order)}
                        data-testid={`view-order-${order.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openInvoice(order.id)}
                        data-testid={`invoice-order-${order.id}`}
                      >
                        <Printer className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Order Details Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Order {selectedOrder?.order_number}
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6 mt-4">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className={`badge-handcraft ${getStatusBadgeClass(selectedOrder.status)}`}>
                  {selectedOrder.status}
                </span>
                <span className="text-sm text-muted-foreground">
                  {new Date(selectedOrder.created_at).toLocaleString()}
                </span>
              </div>

              {/* Customer Info */}
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-medium mb-2">Customer Details</h4>
                <p className="text-sm">{selectedOrder.customer?.name}</p>
                <p className="text-sm text-muted-foreground">{selectedOrder.customer?.email}</p>
                <p className="text-sm text-muted-foreground">{selectedOrder.customer?.phone}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  {selectedOrder.customer?.address}<br />
                  {selectedOrder.customer?.city}, {selectedOrder.customer?.state} - {selectedOrder.customer?.pincode}
                </p>
              </div>

              {/* Items */}
              <div>
                <h4 className="font-medium mb-3">Order Items</h4>
                <div className="space-y-3">
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="flex gap-4 p-3 bg-white rounded-lg border">
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                        {item.product_image && (
                          <img src={item.product_image} alt="" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-sm text-muted-foreground">
                          ₹{item.price?.toLocaleString()} × {item.quantity}
                        </p>
                        {item.is_custom && (
                          <div className="mt-2 p-2 bg-sage/10 rounded text-sm">
                            <span className="badge-handcraft bg-sage text-white text-xs mb-2">Custom Order</span>
                            {item.custom_notes && <p className="mt-1"><strong>Notes:</strong> {item.custom_notes}</p>}
                            {item.custom_preferences && <p><strong>Preferences:</strong> {item.custom_preferences}</p>}
                            {item.custom_image && (
                              <div className="mt-2">
                                <strong>Reference Image:</strong>
                                <img src={item.custom_image} alt="Custom" className="mt-1 w-32 h-32 object-cover rounded" />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <p className="font-medium">₹{item.total?.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Notes */}
              {selectedOrder.notes && (
                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="font-medium mb-2">Order Notes</h4>
                  <p className="text-sm">{selectedOrder.notes}</p>
                </div>
              )}

              {/* Total */}
              <div className="border-t pt-4">
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total</span>
                  <span>₹{selectedOrder.total?.toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog open={!!invoiceOrder} onOpenChange={() => setInvoiceOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="no-print">
            <DialogTitle className="font-serif text-xl flex items-center justify-between">
              Invoice
              <Button onClick={printInvoice} variant="outline" size="sm">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </DialogTitle>
          </DialogHeader>
          {invoiceOrder && (
            <div className="space-y-6 mt-4" id="invoice-content">
              {/* Header */}
              <div className="text-center border-b pb-6">
                <h1 className="font-serif text-3xl text-charcoal">{invoiceOrder.business_details?.name}</h1>
                <p className="font-handwritten text-xl text-accent">{invoiceOrder.business_details?.tagline}</p>
                <p className="text-sm text-muted-foreground mt-2">{invoiceOrder.business_details?.email}</p>
              </div>

              {/* Invoice Info */}
              <div className="flex justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Invoice Number</p>
                  <p className="font-medium">{invoiceOrder.invoice_number}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Date</p>
                  <p className="font-medium">
                    {new Date(invoiceOrder.generated_at).toLocaleDateString('en-IN')}
                  </p>
                </div>
              </div>

              {/* Bill To */}
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Bill To</p>
                <p className="font-medium">{invoiceOrder.order?.customer?.name}</p>
                <p className="text-sm text-muted-foreground">
                  {invoiceOrder.order?.customer?.address}<br />
                  {invoiceOrder.order?.customer?.city}, {invoiceOrder.order?.customer?.state} - {invoiceOrder.order?.customer?.pincode}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {invoiceOrder.order?.customer?.phone}<br />
                  {invoiceOrder.order?.customer?.email}
                </p>
              </div>

              {/* Items Table */}
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 text-xs uppercase tracking-widest text-muted-foreground">Item</th>
                    <th className="text-center py-2 text-xs uppercase tracking-widest text-muted-foreground">Qty</th>
                    <th className="text-right py-2 text-xs uppercase tracking-widest text-muted-foreground">Price</th>
                    <th className="text-right py-2 text-xs uppercase tracking-widest text-muted-foreground">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceOrder.order?.items?.map((item, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-3">
                        <p className="font-medium">{item.product_name}</p>
                        {item.is_custom && (
                          <span className="text-xs text-sage">(Custom Order)</span>
                        )}
                      </td>
                      <td className="text-center py-3">{item.quantity}</td>
                      <td className="text-right py-3">₹{item.price?.toLocaleString()}</td>
                      <td className="text-right py-3 font-medium">₹{item.total?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div className="border-t pt-4">
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>₹{invoiceOrder.order?.subtotal?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="text-sage">Free</span>
                </div>
                <div className="flex justify-between py-2 text-lg font-semibold border-t">
                  <span>Total</span>
                  <span>₹{invoiceOrder.order?.total?.toLocaleString()}</span>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center text-sm text-muted-foreground border-t pt-6">
                <p>Thank you for your order!</p>
                <p className="font-handwritten text-lg text-accent mt-2">Handcrafted with Love</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOrders;
